<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'
import { apiGet, apiPost, apiPut, apiDelete } from '@/stores/api'
import { formatDateShort as formatDate } from '@/utils/formatters'
import {
  ListTodo,
  Plus,
  Circle,
  Trash2,
  Loader2,
  Calendar,
  AlertCircle
} from 'lucide-vue-next'
import logger from '@/utils/logger'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'

interface TodoAssignee {
  id: number
  first_name: string
  last_name: string
  username: string
}

interface StaffTodo {
  id: number
  title: string
  description: string | null
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: string
  due_date: string | null
  completed_at: string | null
  recurrence: string
  created_by: number
  created_by_name: string
  created_at: string
  assignees: TodoAssignee[]
}

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const { confirmDialog, confirm: showConfirm, executeConfirm, cancelConfirm } = useConfirmDialog()

const show = ref(false)
const loading = ref(false)
const todos = ref<StaffTodo[]>([])
const quickAddTitle = ref('')
const isAdding = ref(false)
const editingId = ref<number | null>(null)
const editingTitle = ref('')
const completingId = ref<number | null>(null)
const deletingId = ref<number | null>(null)

const isStaff = computed(() => authStore.isStaff)

const pendingCount = computed(() => todos.value.length)

const priorityColors: Record<string, string> = {
  urgent: 'text-danger-400',
  high: 'text-warning-400',
  medium: 'text-primary-400',
  low: 'text-surface-500'
}

let hideTimeout: ReturnType<typeof setTimeout> | null = null

function handleMouseEnter() {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
  show.value = true
  if (todos.value.length === 0) {
    fetchTodos()
  }
}

function handleMouseLeave() {
  hideTimeout = setTimeout(() => {
    show.value = false
    editingId.value = null
  }, 150)
}

function close() {
  show.value = false
  editingId.value = null
}

async function fetchTodos() {
  loading.value = true
  try {
    const response = await apiGet<{ data: StaffTodo[] }>('/staff-todos?filter=my&status=pending')
    todos.value = response.data || []
  } catch (error) {
    logger.error('Failed to fetch todos:', error)
    notificationStore.error('Failed to load todos')
  } finally {
    loading.value = false
  }
}

async function quickAdd() {
  if (!quickAddTitle.value.trim() || isAdding.value) return

  isAdding.value = true
  try {
    await apiPost('/staff-todos', {
      title: quickAddTitle.value.trim(),
      priority: 'medium',
      assignee_ids: [authStore.user?.id]
    })
    quickAddTitle.value = ''
    notificationStore.success('Todo created')
    await fetchTodos()
  } catch (error) {
    logger.error('Failed to create todo:', error)
    notificationStore.error('Failed to create todo')
  } finally {
    isAdding.value = false
  }
}

function startEditing(todo: StaffTodo) {
  editingId.value = todo.id
  editingTitle.value = todo.title
}

async function saveEdit(todo: StaffTodo) {
  if (!editingTitle.value.trim()) {
    editingId.value = null
    return
  }

  if (editingTitle.value.trim() === todo.title) {
    editingId.value = null
    return
  }

  try {
    await apiPut(`/staff-todos/${todo.id}`, {
      title: editingTitle.value.trim()
    })
    notificationStore.success('Todo updated')
    await fetchTodos()
  } catch (error) {
    logger.error('Failed to update todo:', error)
    notificationStore.error('Failed to update todo')
  } finally {
    editingId.value = null
  }
}

async function completeTodo(todo: StaffTodo) {
  completingId.value = todo.id
  try {
    await apiPost(`/staff-todos/${todo.id}/complete`, {})
    notificationStore.success('Todo completed')
    await fetchTodos()
  } catch (error) {
    logger.error('Failed to complete todo:', error)
    notificationStore.error('Failed to complete todo')
  } finally {
    completingId.value = null
  }
}

function deleteTodo(todo: StaffTodo, e: Event) {
  e.stopPropagation()
  showConfirm({
    title: 'Delete Todo',
    message: 'Delete this todo?',
    confirmText: 'Delete',
    onConfirm: async () => {
      deletingId.value = todo.id
      try {
        await apiDelete(`/staff-todos/${todo.id}`)
        notificationStore.success('Todo deleted')
        await fetchTodos()
      } catch (error) {
        logger.error('Failed to delete todo:', error)
        notificationStore.error('Failed to delete todo')
      } finally {
        deletingId.value = null
      }
    }
  })
}

function isOverdue(todo: StaffTodo): boolean {
  if (!todo.due_date || todo.status === 'completed') return false
  return new Date(todo.due_date) < new Date()
}

function isDueSoon(todo: StaffTodo): boolean {
  if (!todo.due_date || todo.status === 'completed') return false
  const dueDate = new Date(todo.due_date)
  const today = new Date()
  const threeDaysFromNow = new Date()
  threeDaysFromNow.setDate(today.getDate() + 3)
  return dueDate <= threeDaysFromNow && dueDate >= today
}

onMounted(() => {
  // Pre-fetch on mount if staff
  if (isStaff.value) {
    fetchTodos()
  }
})
</script>

<template>
  <div
    v-if="isStaff"
    class="relative"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Trigger Button -->
    <button
      class="relative p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
      title="Quick Todos"
    >
      <ListTodo class="w-5 h-5" />
      <!-- Badge -->
      <span
        v-if="pendingCount > 0"
        class="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold bg-danger-500 text-white rounded-full"
      >
        {{ pendingCount > 99 ? '99+' : pendingCount }}
      </span>
    </button>

    <!-- Dropdown Panel -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="show"
        class="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] bg-surface-800 border border-surface-700 rounded-xl shadow-2xl z-50"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-3 border-b border-surface-700">
          <span class="text-sm font-medium text-surface-100">My Todos</span>
        </div>

        <!-- Quick Add -->
        <div class="p-3 border-b border-surface-700">
          <div class="flex gap-2">
            <input
              v-model="quickAddTitle"
              type="text"
              placeholder="Add a quick todo..."
              class="flex-1 px-3 py-2 text-sm bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500"
              @keydown.enter="quickAdd"
            />
            <button
              @click="quickAdd"
              :disabled="!quickAddTitle.trim() || isAdding"
              class="p-2 bg-primary-600 hover:bg-primary-500 disabled:bg-surface-700 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <Loader2 v-if="isAdding" class="w-4 h-4 text-white animate-spin" />
              <Plus v-else class="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="max-h-[340px] overflow-y-auto">
          <div v-if="loading" class="flex items-center justify-center py-8">
            <Loader2 class="w-5 h-5 animate-spin text-primary-400" />
          </div>

          <div v-else-if="todos.length === 0" class="text-center py-8 text-surface-500 text-sm">
            <Circle class="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No pending todos</p>
          </div>

          <div v-else class="divide-y divide-surface-700">
            <div
              v-for="todo in todos"
              :key="todo.id"
              class="group flex items-start gap-3 p-3 hover:bg-surface-700/50 transition-colors"
            >
              <!-- Checkbox -->
              <button
                @click="completeTodo(todo)"
                :disabled="completingId === todo.id"
                class="flex-shrink-0 mt-0.5 text-surface-500 hover:text-primary-400 disabled:opacity-50 transition-colors"
              >
                <Loader2 v-if="completingId === todo.id" class="w-5 h-5 animate-spin" />
                <Circle v-else class="w-5 h-5" />
              </button>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <!-- Editing mode -->
                <input
                  v-if="editingId === todo.id"
                  :ref="(el) => (el as HTMLInputElement)?.focus()"
                  v-model="editingTitle"
                  type="text"
                  class="w-full px-2 py-1 text-sm bg-surface-900 border border-primary-500 rounded text-surface-100 focus:outline-none"
                  @keydown.enter="saveEdit(todo)"
                  @keydown.escape="editingId = null"
                  @blur="saveEdit(todo)"
                />
                <!-- Display mode -->
                <template v-else>
                  <div
                    @click="startEditing(todo)"
                    class="text-sm text-surface-200 cursor-text hover:text-surface-100"
                  >
                    {{ todo.title }}
                  </div>
                  <div class="flex items-center gap-3 mt-1">
                    <!-- Priority dot -->
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="priorityColors[todo.priority].replace('text-', 'bg-')"
                    ></span>
                    <!-- Due date -->
                    <span
                      v-if="todo.due_date"
                      class="flex items-center gap-1 text-xs"
                      :class="{
                        'text-danger-400': isOverdue(todo),
                        'text-warning-400': isDueSoon(todo),
                        'text-surface-500': !isOverdue(todo) && !isDueSoon(todo)
                      }"
                    >
                      <Calendar class="w-3 h-3" />
                      {{ formatDate(todo.due_date) }}
                      <AlertCircle v-if="isOverdue(todo)" class="w-3 h-3" />
                    </span>
                  </div>
                </template>
              </div>

              <!-- Delete button -->
              <button
                @click="deleteTodo(todo, $event)"
                :disabled="deletingId === todo.id"
                class="flex-shrink-0 p-1.5 text-surface-600 hover:text-danger-400 hover:bg-danger-500/10 rounded opacity-0 group-hover:opacity-100 disabled:opacity-50 transition-all"
              >
                <Loader2 v-if="deletingId === todo.id" class="w-4 h-4 animate-spin" />
                <Trash2 v-else class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-3 pb-3 pt-2 border-t border-surface-700">
          <router-link
            to="/todos"
            @click="close"
            class="block w-full text-center text-xs text-primary-400 hover:text-primary-300 py-2 hover:bg-surface-700/50 rounded transition-colors"
          >
            View All Todos
          </router-link>
        </div>
      </div>
    </Transition>
    <ConfirmDialog v-bind="confirmDialog" @confirm="executeConfirm" @cancel="cancelConfirm" />
  </div>
</template>
